import { useEffect, useState, useCallback } from 'react';

import { format } from 'date-fns/format';

import { DetailDateInput } from 'components/DateInput';
import EopyyDetail from 'components/Eopyy/EopyyDetail';
import { getLinkHref } from 'components/Link/Link';
import DetailStringList from 'components/List/DetailStringList';
import { CustomInput } from 'components/shared/Input/Input.style';
import Toggle from 'components/Toggle/Toggle';
import {
  balanceFloatDecimals,
  formatCurrency,
  removeDemicals,
} from 'helpers/convertNumber';
import { useGetSelectorData } from 'hooks/useGetListData';

type EditableCellProps = {
  row: any;
  column: any;
  value: any;
  handleTableUpdate: (args: any) => void;
  handleEopyySuccess: (args: any) => void;
};

export const EditableCell = ({
  row,
  column,
  value: initialValue,
  handleTableUpdate, // This is a custom function that we supplied to our table instance
  handleEopyySuccess,
}: EditableCellProps) => {
  const fieldCaption = column.Header;
  const { ID, fields = {} } = row.original;

  const isEditable = Array.isArray(fields.editable)
    ? fields.editable.some((item: any) => item.FIELDCAPTION === fieldCaption)
    : false;
  const editableField = Array.isArray(fields.editable)
    ? fields.editable.find((item: any) => item.FIELDCAPTION === fieldCaption)
    : null;
  const isBoolean = editableField?.FIELDTYPE === 'Bool';
  const isEopyy = Array.isArray(fields.eopyy)
    ? fields.eopyy.find((eopyyField: any) => eopyyField.FIELDCAPTION === fieldCaption)
    : false;
  const isDate = Array.isArray(fields.date)
    ? fields.date.some((item: any) => item.FIELDCAPTION === fieldCaption)
    : false;
  const isTime = Array.isArray(fields.time)
    ? fields.time.some((item: any) => item.FIELDCAPTION === fieldCaption)
    : false;
  const isString = Array.isArray(fields.string)
    ? fields.string.some((item: any) => item.FIELDCAPTION === fieldCaption)
    : false;
  const isList = Array.isArray(fields.list)
    ? fields.list.some((item: any) => item.FIELDCAPTION === fieldCaption)
    : false;
  const isReadOnly = Array.isArray(fields.readOnly)
    ? fields.readOnly.some((item: any) => item.FIELDCAPTION === fieldCaption)
    : false;
  const isLink = Array.isArray(fields.link)
    ? fields.link.some((item: any) => item.FIELDCAPTION === fieldCaption)
    : false;

  // Gift lines should be read-only
  const isGiftLine = row.original.ISGIFT;

  // We need to keep and update the state of the cell normally
  const fieldProperty = editableField?.FIELDPROPERTY;
  // Decimal fields default to 2 decimals when NUMOFDECIMAL is empty/0/missing.
  const configuredDecimals = Number(editableField?.NUMOFDECIMAL);
  const decimals = configuredDecimals > 0 ? configuredDecimals : 2;

  const normalizeNumeric = (raw: any) => {
    if (fieldProperty === 'Number')
      return balanceFloatDecimals(formatCurrency(raw, decimals), decimals);
    if (fieldProperty === 'Integer') return removeDemicals(formatCurrency(raw, 0));
    return raw;
  };

  const [value, setValue] = useState(initialValue);
  const onChange = (e: any) => setValue(e.target.value);
  // We'll only update the external data when the input is blurred
  const onBlur = (e: any) => {
    const normalized = normalizeNumeric(e.target.value);
    setValue(normalized);
    const selectedValue = { FIELDVALUE: normalized, DISPLAYVALUE: normalized };
    handleTableUpdate({ storeId: ID, fieldCaption, value: selectedValue });
  };
  const handleFieldUpdate = useCallback(
    (selectedValue: any, displayValue: any) => {
      handleTableUpdate({
        storeId: ID,
        fieldCaption,
        value: {
          FIELDVALUE: selectedValue,
          DISPLAYVALUE: displayValue || selectedValue,
        },
      });
    },
    [ID, fieldCaption, handleTableUpdate],
  );
  // If the initialValue is changed external, sync it up with our state
  useEffect(() => setValue(initialValue), [initialValue]);

  if (!isEditable || isReadOnly) {
    if (value instanceof Date) {
      return format(value, isTime ? 'HH:mm' : 'dd/MM/yyyy');
    }
    if (isLink && value) {
      return (
        <a
          href={getLinkHref(value)}
          target='_blank'
          rel='noopener noreferrer'
          onClick={(e) => e.stopPropagation()}
          style={{ color: 'blue', textDecoration: 'underline' }}
        >
          {value}
        </a>
      );
    }
    return value || '';
  }
  if (isDate) {
    return <DetailDateInput {...{ value, handleFieldUpdate }} />;
  }
  if ((isString || isList) && editableField) {
    return (
      <DetailStringList
        value={value}
        editableField={editableField}
        handleFieldUpdate={handleFieldUpdate}
        useSelectorData={isList ? useGetSelectorData : undefined}
      />
    );
  }
  if (isEopyy) {
    return (
      <EopyyDetail
        storeId={ID}
        eopyyInfo={isEopyy.eopyyInfo}
        handleUpdate={handleFieldUpdate}
        handleSuccess={handleEopyySuccess}
        {...{ value, onChange }}
      />
    );
  }
  if (isEditable && isBoolean) {
    const isYesActive = value === 'Yes' || value === '1';
    const isNoActive = value === 'No' || value === '0';
    const handleButtonClick = (newValue: any) => {
      if ((newValue === '1' && !isYesActive) || (newValue === '0' && !isNoActive)) {
        onChange({ target: { value: newValue } });
        onBlur({ target: { value: newValue } });
      }
    };
    const yesButtonStyle = {
      width: '30px',
      padding: '2px 2px',
      marginRight: '2px',
      border: '0.5px solid rgba(0,0,0,0.3)',
      backgroundColor: isYesActive ? 'rgba(7,157,214,0.5)' : 'transparent',
    };
    const noButtonStyle = {
      width: '30px',
      alignItems: 'center',
      padding: '2px 2px',
      border: '0.5px solid rgba(0,0,0,0.3)',
      backgroundColor: isNoActive ? 'rgba(7,157,214,0.5)' : 'transparent',
    };
    return (
      <div style={isGiftLine ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
        <Toggle
          onClick={
            isGiftLine ? () => {} : () => handleButtonClick(isYesActive ? '0' : '1')
          }
          isActive={Boolean(isYesActive)}
        />
      </div>
    );

    return (
      <div>
        <button style={yesButtonStyle} onClick={() => handleButtonClick('1')}>
          &#10003;
        </button>
        <button style={noButtonStyle} onClick={() => handleButtonClick('0')}>
          &#10007;
        </button>
      </div>
    );
  }
  const isStringField = editableField?.FIELDTYPE === 'String';
  const numericInputProps =
    fieldProperty === 'Integer'
      ? { inputMode: 'numeric' as const, pattern: '[0-9]*' }
      : fieldProperty === 'Number'
        ? { inputMode: 'decimal' as const, pattern: '[0-9]*[.,]?[0-9]*' }
        : {};
  return (
    <CustomInput
      style={{
        color: isLink && value ? 'blue' : 'inherit',
        textDecoration: isLink && value ? 'underline' : undefined,
        boxShadow: 'none',
        backgroundColor: 'inherit',
        ...(isStringField && { padding: 0, border: 'none' }),
        ...(isGiftLine && { opacity: 0.5, pointerEvents: 'none' }),
      }}
      className={isStringField ? '' : 'tableInputCell'}
      {...numericInputProps}
      {...{
        value,
        onChange: isGiftLine ? () => {} : onChange,
        onBlur: isGiftLine ? () => {} : onBlur,
        onClick:
          isLink && !isGiftLine
            ? (e: React.MouseEvent<HTMLInputElement>) => {
                if (e.metaKey || e.ctrlKey) {
                  e.preventDefault();
                  e.stopPropagation();
                  const href = getLinkHref(value);
                  if (href) window.open(href, '_blank', 'noopener,noreferrer');
                }
              }
            : undefined,
      }}
    />
  );
};
